<template>
    <v-container>
        <v-row>
            <v-col cols="12">
                <h1>Sequenced Note Player Debug</h1>
                <p class="text-body-2">
                    Test the SequencedNotePlayer component with configurable sequences
                    and settings.
                </p>
            </v-col>
        </v-row>

        <!-- Configuration -->
        <v-row>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Configuration</v-card-title>
                    <v-card-text>
                        <v-textarea
                            v-model="sequenceInput"
                            label="Sequence (comma-separated notes)"
                            hint="E.g., C4, D4, E4, F4, G4"
                            persistent-hint
                            rows="3"
                        />

                        <v-slider
                            v-model="toleranceCents"
                            label="Tolerance (cents)"
                            min="10"
                            max="45"
                            step="5"
                            thumb-label
                            hint="Max 45 to prevent overlap"
                            persistent-hint
                            class="mt-4"
                        />

                        <v-checkbox
                            v-model="allowOctaveErrors"
                            label="Allow octave errors"
                            hint="Accept C5 when expecting C4"
                            persistent-hint
                        />

                        <v-checkbox
                            v-model="showFingerings"
                            label="Show fingerings"
                            persistent-hint
                        />

                        <v-checkbox
                            v-model="showPitchVisualizer"
                            label="Show pitch visualizer"
                            hint="Display real-time pitch feedback"
                            persistent-hint
                        />

                        <v-select
                            v-if="showPitchVisualizer"
                            v-model="pitchVisualizerVariant"
                            label="Visualizer Style"
                            :items="[
                                { title: 'Default (full gradient)', value: 'default' },
                                { title: 'Minimal (discrete)', value: 'minimal' }
                            ]"
                            density="compact"
                            class="mt-2"
                        />

                        <v-slider
                            v-model="dropoutBufferFrames"
                            label="Dropout Buffer (frames)"
                            min="0"
                            max="20"
                            step="1"
                            thumb-label
                            hint="Frames of silence to ignore"
                            persistent-hint
                            class="mt-4"
                        />

                        <div class="mt-4">
                            <v-btn color="primary" @click="applySequence" block>
                                Apply Configuration
                            </v-btn>
                        </div>

                        <!-- Pitch Recorder -->
                        <div class="mt-4 pa-3" style="background: rgba(0,0,0,0.03); border-radius: 8px;">
                            <div class="text-caption text-medium-emphasis mb-2">Recording</div>
                            <PitchRecorderWidget
                                ref="recorderWidget"
                                exercise-type="sequenced-note-player"
                                :exercise-config="{
                                    sequence: parsedSequence,
                                    toleranceCents,
                                    allowOctaveErrors,
                                    showFingerings,
                                    dropoutBufferFrames
                                }"
                            />
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Quick Presets -->
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Quick Presets</v-card-title>
                    <v-card-text>
                        <v-btn
                            v-for="preset in presets"
                            :key="preset.name"
                            color="secondary"
                            variant="outlined"
                            @click="loadPreset(preset)"
                            block
                            class="mb-2"
                        >
                            {{ preset.name }}
                        </v-btn>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Player Display -->
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>Sequenced Note Player</v-card-title>
                    <v-card-text>
                        <SequencedNotePlayer
                            v-if="currentSequence.length > 0"
                            :key="playerKey"
                            :sequence="currentSequence"
                            :tolerance-cents="toleranceCents"
                            :allow-octave-errors="allowOctaveErrors"
                            :show-fingerings="showFingerings"
                            :show-pitch-visualizer="showPitchVisualizer"
                            :pitch-visualizer-variant="pitchVisualizerVariant"
                            :dropout-buffer-frames="dropoutBufferFrames"
                            @status-change="handleStatusChange"
                            @note-matched="handleNoteMatched"
                            @wrong-note="handleWrongNote"
                            @sequence-complete="handleSequenceComplete"
                            @pitch-detected="handlePitchDetected"
                        />
                        <div v-else class="text-center pa-4 text-medium-emphasis">
                            Enter a sequence and click "Apply Configuration" to begin
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Event Log -->
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title>
                        Event Log
                        <v-spacer />
                        <v-btn size="small" @click="events = []">Clear</v-btn>
                    </v-card-title>
                    <v-card-text>
                        <v-list dense class="event-log">
                            <v-list-item
                                v-for="(event, index) in events"
                                :key="index"
                                :class="`event-${event.type}`"
                            >
                                <v-list-item-title>
                                    <v-icon :color="getEventColor(event.type)" size="small">
                                        {{ getEventIcon(event.type) }}
                                    </v-icon>
                                    {{ event.message }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ event.timestamp }}
                                </v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item v-if="events.length === 0">
                                <v-list-item-title class="text-medium-emphasis">
                                    No events yet
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import SequencedNotePlayer from '../components/SequencedNotePlayer.vue';
import PitchRecorderWidget from '../pitch/components/PitchRecorderWidget.vue';
import type { SequenceMatchEvent, PitchDetectionResult } from '../pitch';

interface Preset {
    name: string;
    sequence: string;
}

interface LogEvent {
    type: string;
    message: string;
    timestamp: string;
}

// Configuration
const sequenceInput = ref('C4, D4, E4, F4, G4');
const toleranceCents = ref(40);
const allowOctaveErrors = ref(false);
const showFingerings = ref(true);
const showPitchVisualizer = ref(false);
const pitchVisualizerVariant = ref<'default' | 'minimal'>('minimal');
const dropoutBufferFrames = ref(5);

// Player state
const currentSequence = ref<string[]>([]);
const playerKey = ref(0); // Force re-render on config change

// Recorder widget
const recorderWidget = ref<InstanceType<typeof PitchRecorderWidget> | null>(null);

// Computed: parse sequence input in real-time for recording metadata
const parsedSequence = computed(() => {
    return sequenceInput.value
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
});

// Event log
const events = ref<LogEvent[]>([]);

// Presets
const presets: Preset[] = [
    { name: 'C Major Scale', sequence: 'C4, D4, E4, F4, G4, A4, B4, C5' },
    { name: 'G Major Scale', sequence: 'G4, A4, B4, C5, D5, E5, F#5, G5' },
    { name: 'C Major Arpeggio', sequence: 'C4, E4, G4, C5' },
    { name: 'Simple Pattern', sequence: 'C4, D4, C4, D4, C4' },
    { name: 'Melodic Pattern', sequence: 'C4, D4, E4, F4, G4, F4, E4, D4, C4' },
    { name: 'Repeated Notes', sequence: 'F6, F6, F6, F6' },
];

// Apply configuration
function applySequence() {
    const sequence = sequenceInput.value
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

    if (sequence.length < 2) {
        alert('Please enter at least 2 notes');
        return;
    }

    currentSequence.value = sequence;
    playerKey.value++; // Force re-render
    events.value = []; // Clear log
}

// Load preset
function loadPreset(preset: Preset) {
    sequenceInput.value = preset.sequence;
    applySequence();
}

// Event handlers
function handleStatusChange(status: string, event: SequenceMatchEvent | null) {
    // Don't log every status change, only significant events
}

function handleNoteMatched(note: string, index: number) {
    logEvent('note-matched', `✓ Matched: ${note} (index ${index})`);
}

function handleWrongNote(detected: string, expected: string) {
    logEvent('wrong-note', `✗ Wrong note: played ${detected}, expected ${expected}`);
}

function handleSequenceComplete(wrongAttempts: number) {
    logEvent('sequence-complete', `🎉 Sequence complete! ${wrongAttempts} wrong attempts`);
}

function handlePitchDetected(result: PitchDetectionResult) {
    recorderWidget.value?.capture(result);
}

function logEvent(type: string, message: string) {
    const timestamp = new Date().toLocaleTimeString();
    events.value.unshift({ type, message, timestamp });

    // Keep log size manageable
    if (events.value.length > 50) {
        events.value.pop();
    }
}

function getEventColor(type: string): string {
    switch (type) {
        case 'note-matched':
            return 'success';
        case 'wrong-note':
            return 'error';
        case 'sequence-complete':
            return 'primary';
        default:
            return 'grey';
    }
}

function getEventIcon(type: string): string {
    switch (type) {
        case 'note-matched':
            return 'mdi-check-circle';
        case 'wrong-note':
            return 'mdi-close-circle';
        case 'sequence-complete':
            return 'mdi-trophy';
        default:
            return 'mdi-circle';
    }
}
</script>

<style scoped>
.event-log {
    max-height: 400px;
    overflow-y: auto;
}

.event-note-matched {
    background: rgba(76, 175, 80, 0.1);
}

.event-wrong-note {
    background: rgba(244, 67, 54, 0.1);
}

.event-sequence-complete {
    background: rgba(33, 150, 243, 0.1);
}
</style>
